import { AsyncPipe, NgTemplateOutlet } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatIconButton } from '@angular/material/button'
import { MatCheckbox } from '@angular/material/checkbox'
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatInput } from '@angular/material/input'
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu'
import { MatTab, MatTabGroup } from '@angular/material/tabs'
import { MatToolbar } from '@angular/material/toolbar'
import {
  ElderHeaderComponent,
  ElderPaneActionsComponent,
  ElderPaneContentComponent,
  ElderPaneHeaderComponent,
  ElderPaneTitleComponent,
  ElderSelectComponent,
  ElderSinglePaneWrapperComponent,
  ElderTabGroupRoutingDirective,
  ElderThemeDirective,
  ElderToastService,
  ElderValidationErrorDirective,
} from '@elderbyte/ngx-starter'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject, Observable } from 'rxjs'
import { IdValidator } from '../../../../features/validators/id-validator'
import { FacilityContextService } from '../../../context/facility-context/facility-context.service'
import { FacilitySelectDirective } from '../../../facilities/facility-select.directive'
import { PropertyConf } from '../../../properties/conf/property-conf'
import { PropertyConfTableComponent } from '../../../properties/conf/property-conf-table/property-conf-table.component'
import { PropertyUtil } from '../../../properties/property-util'
import { WorkStationTypeSelectDirective } from '../types/work-station-type-select/work-station-type-select.directive'
import { WorkStationTypeService } from '../types/work-station-type.service'
import { WorkStation, WorkStationGateway } from '../work-station'
import { WorkStationConfigurationBrowserComponent } from '../work-station-configuration-browser/work-station-configuration-browser.component'
import { WorkStationService } from '../work-station.service'

@Component({
  selector: 'app-work-station-detail',
  templateUrl: './work-station-detail.component.html',
  styleUrls: ['./work-station-detail.component.scss'],
  imports: [
    ElderSinglePaneWrapperComponent,
    ElderPaneHeaderComponent,
    ElderPaneTitleComponent,
    ElderPaneActionsComponent,
    ElderPaneContentComponent,
    MatToolbar,
    ElderThemeDirective,
    MatIconButton,
    MatIcon,
    NgTemplateOutlet,
    MatTabGroup,
    ElderTabGroupRoutingDirective,
    MatTab,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    FormsModule,
    MatFormField,
    ElderSelectComponent,
    FacilitySelectDirective,
    MatLabel,
    MatInput,
    MatError,
    ElderValidationErrorDirective,
    WorkStationTypeSelectDirective,
    PropertyConfTableComponent,
    MatCheckbox,
    WorkStationConfigurationBrowserComponent,
    ElderHeaderComponent,
    AsyncPipe,
    ElderTranslatePipe,
  ],
})
export class WorkStationDetailComponent implements OnInit {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly _workStation$: BehaviorSubject<WorkStation> = new BehaviorSubject(null)

  private _creationMode: boolean

  @Input()
  public embedded = false

  @Output()
  public close: EventEmitter<Event> = new EventEmitter<Event>()

  public properties$ = new BehaviorSubject<PropertyConf[]>([])

  public readonly idValidatorPattern = IdValidator.pattern

  private workStationService = inject(WorkStationService)
  private workStationTypeService = inject(WorkStationTypeService)
  private appContextService = inject(FacilityContextService)
  private toastService = inject(ElderToastService)

  /***************************************************************************
   *                                                                         *
   * Life Cycle                                                              *
   *                                                                         *
   **************************************************************************/

  public ngOnInit(): void {
    if (!this.workStation) {
      this.workStation = this.initNewWorkStation()
    }
  }

  /***************************************************************************
   *                                                                         *
   * Getter / Setters                                                        *
   *                                                                         *
   **************************************************************************/

  public get workStation$(): Observable<WorkStation> {
    return this._workStation$.asObservable()
  }

  public get workStation(): WorkStation {
    return this._workStation$.getValue()
  }

  @Input()
  public set workStation(workStation: WorkStation) {
    if (workStation) {
      this._creationMode = !workStation.id
      if (!workStation.gateway) {
        workStation.gateway = new WorkStationGateway()
      }
      this._workStation$.next(workStation)

      if (!this.creationMode) {
        this.workStationTypeService.findById(workStation.type).subscribe({
          next: (type) => {
            PropertyUtil.populate(workStation.properties, type.propertySpecs) // mutates workStation, which is referenced in BehaviorSubject
            this.properties$.next(workStation.properties)
          },
          error: (err) => this.toastService.error('Could not load WorkStation type!', err),
        })
      }
    }
  }

  public get creationMode(): boolean {
    return this._creationMode
  }

  public get deletionEnabled(): boolean {
    return !this.creationMode
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public onSave(event: Event): void {
    this.save()
  }

  public onDelete(event: Event): void {
    this.delete()
  }

  public onClose(event: Event): void {
    this.emitClose(event)
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private save(): void {
    this.workStation.properties = this.properties$.getValue()

    this.workStationService.save(this.workStation).subscribe({
      next: (workStation) => {
        this.workStation = workStation
        this.toastService.info('messages.persisting_successful')
      },
      error: (err) => this.toastService.error('messages.saving_failed', err),
    })
  }

  private delete(): void {
    this.workStationService.delete(this.workStation).subscribe({
      next: () => this.emitClose(),
      error: (err) => this.toastService.error('messages.deleting_failed', err),
    })
  }

  private emitClose(event?: Event): void {
    this.close.emit(event)
  }

  private initNewWorkStation(): WorkStation {
    const workStation = new WorkStation()
    const currentFacility = this.appContextService.facility
    if (currentFacility) {
      workStation.facility = currentFacility.id
    }
    return workStation
  }
}
